/* ARDAS - Book Mezure appointment flow
 * ---------------------------------------------------------------------------
 * Vanilla JS, no dependencies. Orchestrates:
 *   - Appointment type: Ardas Studio (₹1,500 fee) vs Home / Office (₹5,000 visiting charge)
 *   - Post-purchase (Flow A): ?purchased=1 -> never charge a visiting fee
 *   - Pincode service-area gate for Home / Office (unchanged data below)
 *   - Address capture for Home / Office
 *   - Existing vs new Mezure choice
 *   - Payment boundary (Flow B, Home/Office): stops at a Razorpay-ready step.
 *     NO payment is faked - the pay button stays disabled and no confirmation
 *     is shown until real Razorpay credentials are connected (see ARDAS_PAYMENT).
 *
 * SHOPIFY MIGRATION (pincode data): replace the CONFIG literal with values
 * injected from a shop metafield, e.g.
 *   window.ARDAS_BOOKING = { HOME_PINCODES: {{ shop.metafields.ardas.home_pincodes | json }}, ... };
 * This file already prefers window.ARDAS_BOOKING when present.
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';

  /* === CONFIG (service-area pincodes, unchanged) ========================== */
  var SERVICE_AREA = [
    '400001', '400002', '400004', '400005', '400006', '400007', '400008',
    '400020', '400021', '400023', '400025', '400026', '400028', '400030',
    '400031', '400033', '400034', '400036', '400038', '400039', '400013', '400018',
    '400050', '400051', '400052', '400054', '400055', '400056', '400057',
    '400058', '400049', '400053', '400047', '400059', '400060', '400061',
    '400062', '400063', '400064', '400065', '400104',
    '400014', '400015', '400016', '400017', '400019', '400022', '400024',
    '400027', '400029', '400031', '400037',
    '400071', '400072', '400074', '400075', '400077', '400079', '400042',
    '400043', '400088', '400089',
    '400076', '400078', '400080', '400081', '400082', '400083', '400084',
    '400086', '400087',
    '400601', '400602', '400603', '400604', '400606', '400607', '400610',
    '400615', '400617', '400618', '400619',
    '400703', '400705', '400706', '400708', '400709', '400610', '400611',
    '400614', '400701', '400702', '400704',
    '400051', '400052', '400050', '400055', '400054'
  ];
  var UNIQUE_SERVICE_AREA = SERVICE_AREA.filter(function (pin, i) { return SERVICE_AREA.indexOf(pin) === i; });
  var DEFAULT_CONFIG = { HOME_PINCODES: UNIQUE_SERVICE_AREA, OFFICE_PINCODES: UNIQUE_SERVICE_AREA };
  var CONFIG = (window.ARDAS_BOOKING && typeof window.ARDAS_BOOKING === 'object') ? window.ARDAS_BOOKING : DEFAULT_CONFIG;
  var PINCODE_LISTS = { 'Home': 'HOME_PINCODES', 'Office': 'OFFICE_PINCODES' };

  function requiresPincode(type) { return Object.prototype.hasOwnProperty.call(PINCODE_LISTS, type); }
  function approvedList(type) { var k = PINCODE_LISTS[type]; var l = k && CONFIG[k]; return Array.isArray(l) ? l : []; }

  // Flow B fee per appointment type, in rupees. Display only: the backend forces
  // the real amount by type when it creates the Razorpay order.
  var CHARGES = { 'Ardas Studio': 1500, 'Home': 5000, 'Office': 5000 };
  function chargeFor(type) { return Object.prototype.hasOwnProperty.call(CHARGES, type) ? CHARGES[type] : 0; }
  function inr(n) { return '₹' + Number(n).toLocaleString('en-IN'); }

  /* === Razorpay connection point =========================================
   * Flow: create order (server) -> Razorpay Checkout (client, public key only)
   * -> verify signature (server) -> confirm appointment. The Key SECRET lives
   * ONLY on the backend; nothing here creates orders or verifies signatures
   * client-side. `configured` is true only when a backend URL + public Key ID
   * + the Razorpay script are all present - otherwise the Pay button stays
   * disabled and no payment/confirmation is ever faked. */
  var PAY_CFG = (window.ARDAS_PAYMENT_CONFIG && typeof window.ARDAS_PAYMENT_CONFIG === 'object') ? window.ARDAS_PAYMENT_CONFIG : {};

  function paymentReady() {
    return !!(PAY_CFG.apiBase && PAY_CFG.keyId && typeof window.Razorpay === 'function');
  }
  function apiUrl(path) {
    return String(PAY_CFG.apiBase).replace(/\/+$/, '') + path;
  }

  window.ARDAS_PAYMENT = window.ARDAS_PAYMENT || {};
  window.ARDAS_PAYMENT.configured = paymentReady();

  /* pay(context) — context: { appointment, onSuccess(res), onFailure(err) }.
   * Returns false (and does nothing) unless the backend is configured. */
  window.ARDAS_PAYMENT.pay = function (context) {
    context = context || {};
    var onSuccess = typeof context.onSuccess === 'function' ? context.onSuccess : function () {};
    var onFailure = typeof context.onFailure === 'function' ? context.onFailure : function () {};
    if (!paymentReady()) { onFailure(new Error('Payment backend is not configured yet.')); return false; }

    // 1) Create the Razorpay order server-side (the server forces the fee by appointment type).
    fetch(apiUrl('/create-order'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        amount: PAY_CFG.amountPaise || 500000,
        currency: PAY_CFG.currency || 'INR',
        appointment: context.appointment || {}
      })
    })
      .then(function (r) { if (!r.ok) throw new Error('create-order failed'); return r.json(); })
      .then(function (order) {
        if (!order || !order.order_id) throw new Error('No order_id returned by backend.');
        // 2) Open Razorpay Checkout with the PUBLIC key + server order_id only.
        var rzp = new window.Razorpay({
          key: PAY_CFG.keyId,
          order_id: order.order_id,
          amount: order.amount || PAY_CFG.amountPaise || 500000,
          currency: order.currency || PAY_CFG.currency || 'INR',
          name: PAY_CFG.name || 'ARDAS',
          description: context.description || PAY_CFG.description || 'Mezure visiting charge',
          prefill: {
            name: (context.appointment && context.appointment.name) || '',
            email: (context.appointment && context.appointment.email) || '',
            contact: (context.appointment && context.appointment.phone) || ''
          },
          handler: function (resp) {
            // 3) Verify the signature server-side, then confirm the appointment.
            fetch(apiUrl('/verify'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
                appointment: context.appointment || {}
              })
            })
              .then(function (r) { if (!r.ok) throw new Error('verify failed'); return r.json(); })
              .then(function (out) {
                if (out && out.verified) onSuccess(out); // only a verified payment confirms
                else onFailure(new Error('Payment could not be verified.'));
              })
              .catch(onFailure);
          },
          modal: { ondismiss: function () { onFailure(new Error('Payment cancelled.')); } }
        });
        rzp.on('payment.failed', function (e) { onFailure(new Error((e && e.error && e.error.description) || 'Payment failed.')); });
        rzp.open();
      })
      .catch(onFailure);
    return true;
  };

  document.addEventListener('DOMContentLoaded', function () {
    var form = document.querySelector('[data-booking-form]');
    if (!form) return;

    var typeGroup = form.querySelector('[data-appointment-type]');
    var locationFields = form.querySelector('[data-location-fields]');
    var pinField = form.querySelector('[data-pincode-field]');
    var pinInput = form.querySelector('[data-pincode-input]');
    var pinMsg = form.querySelector('[data-pincode-msg]');
    var chargeCopy = form.querySelector('[data-charge-copy]');
    var gated = form.querySelector('[data-booking-gated]');
    var typeNote = form.querySelector('[data-type-note]');
    var flowInput = form.querySelector('[data-flow]');
    var chargeInput = form.querySelector('[data-visiting-charge]');
    var banner = form.querySelector('[data-postpurchase-note]');
    var paymentStep = document.querySelector('[data-payment-step]');
    var payAmount = paymentStep && paymentStep.querySelector('[data-pay-amount]');
    var payLabel = paymentStep && paymentStep.querySelector('[data-pay-label]');
    var payBtnLabel = paymentStep && paymentStep.querySelector('[data-pay-btn-label]');
    var payVisitCopy = paymentStep && paymentStep.querySelector('[data-pay-visit-copy]');
    var payStudioCopy = paymentStep && paymentStep.querySelector('[data-pay-studio-copy]');
    var payBack = document.querySelector('[data-pay-back]');
    var mezureNote = form.querySelector('[data-mezure-note]');

    // Flow A: purchase-first customers arrive with ?purchased=1 (set by the
    // order-status "continue to appointment" link). They are never charged.
    var params = new URLSearchParams(window.location.search || '');
    var postPurchase = params.get('purchased') === '1' || params.get('flow') === 'a';
    if (flowInput) flowInput.value = postPurchase ? 'A' : 'B';
    if (postPurchase) {
      if (banner) banner.hidden = false;
      // Flow A: replace the static "₹5,000 visiting charge" badge(s) in the aside
      // with a complimentary label (purchase-first customers are never charged).
      document.querySelectorAll('.booking .btype__tag').forEach(function (tag) {
        if (/₹|5,?000|charge|premium/i.test(tag.textContent)) tag.textContent = 'Complimentary';
      });
    }

    var emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    var verified = false;

    function selectedType() {
      var a = typeGroup && typeGroup.querySelector('.chip.active');
      return a ? (a.getAttribute('data-type') || a.textContent.trim()) : 'Ardas Studio';
    }

    // A fee applies to every Flow B appointment (Studio ₹1,500, Home/Office ₹5,000);
    // post-purchase (Flow A) visits are always complimentary.
    function chargeApplies() { return chargeFor(selectedType()) > 0 && !postPurchase; }

    function setMessage(text, state) {
      if (!pinMsg) return;
      // The pincode status line doubles as the pincode field's error message.
      var isError = !!text && state === 'error';
      if (pinInput) {
        if (!pinMsg.id) pinMsg.id = 'booking-pincode-msg';
        if (isError) pinInput.setAttribute('aria-invalid', 'true'); else pinInput.removeAttribute('aria-invalid');
        describe(pinInput, pinMsg.id, !!text);
      }
      if (pinField) pinField.classList.toggle('is-invalid', isError);
      if (!text) { pinMsg.hidden = true; pinMsg.textContent = ''; pinMsg.removeAttribute('data-state'); return; }
      pinMsg.textContent = text; pinMsg.setAttribute('data-state', state || ''); pinMsg.hidden = false;
    }
    function showGated(show) { if (gated) gated.hidden = !show; }

    /* --- Field errors: red underline + message below the field (a11y-linked) ---
     * markInvalid(el, msg) flags the field; clearInvalid(el) restores it. The
     * message element is created once per field and linked via aria-describedby. */
    function fieldWrap(el) { return el.closest('.field') || el.closest('.booking__consent') || el.parentNode; }
    function errFor(el, create) {
      var id = (el.id || el.name) + '-err';
      var msg = document.getElementById(id);
      if (!msg && create) {
        msg = document.createElement('p');
        msg.id = id; msg.className = 'booking__err'; msg.hidden = true;
        fieldWrap(el).appendChild(msg);
      }
      return msg;
    }
    function describe(el, id, on) {
      var ids = (el.getAttribute('aria-describedby') || '').split(/\s+/).filter(function (x) { return x && x !== id; });
      if (on) ids.push(id);
      if (ids.length) el.setAttribute('aria-describedby', ids.join(' ')); else el.removeAttribute('aria-describedby');
    }
    function markInvalid(el, text) {
      if (!el) return;
      var msg = errFor(el, true);
      msg.textContent = text; msg.hidden = false;
      el.setAttribute('aria-invalid', 'true');
      describe(el, msg.id, true);
      fieldWrap(el).classList.add('is-invalid');
    }
    function clearInvalid(el) {
      if (!el) return;
      var msg = errFor(el, false);
      if (msg) { msg.hidden = true; msg.textContent = ''; describe(el, msg.id, false); }
      el.removeAttribute('aria-invalid');
      fieldWrap(el).classList.remove('is-invalid');
    }

    function todayISO() {
      var d = new Date();
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    }
    // Returns the error text for a field, or '' when it is valid.
    var CHECKS = {
      name: function (v) { return v ? '' : 'Please enter your name.'; },
      phone: function (v) {
        if (!v) return 'Please enter your phone or WhatsApp number.';
        var digits = v.replace(/\D/g, '');
        return /^[+\d][\d\s()-]*$/.test(v) && digits.length >= 10 && digits.length <= 15 ? '' : 'Please enter a valid phone number (10 digits, with country code if outside India).';
      },
      email: function (v) {
        if (!v) return 'Please enter your email address.';
        return emailRe.test(v) ? '' : 'Please enter a valid email address, e.g. name@example.com.';
      },
      preferred_date: function (v, el) {
        if (!v) return el.validity && el.validity.badInput ? 'Please enter a valid date.' : '';
        return v < todayISO() ? 'Please choose today or a later date.' : '';
      },
      preferred_time: function (v) { return v ? '' : 'Please choose a preferred time.'; },
      address: function (v) { return v ? '' : 'Please enter the address where we should visit you.'; }
    };
    function checkField(el) {
      var check = el && CHECKS[el.name];
      if (!check) return '';
      var text = check(el.value.trim(), el);
      if (text) markInvalid(el, text); else clearInvalid(el);
      return text;
    }
    // Clear an error as soon as the value becomes valid (re-check only flagged fields).
    form.addEventListener('input', function (e) { if (e.target.getAttribute('aria-invalid') === 'true') checkField(e.target); });
    form.addEventListener('change', function (e) {
      if (e.target.matches('[data-consent]') && e.target.checked) clearInvalid(e.target);
      else if (e.target.getAttribute('aria-invalid') === 'true') checkField(e.target);
    });
    function focusFirst(el) {
      if (!el) return;
      try { el.focus({ preventScroll: true }); } catch (err) { el.focus(); }
      fieldWrap(el).scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    function syncCharge() {
      var type = selectedType();
      var amt = chargeApplies() ? chargeFor(type) : 0;
      var studio = !requiresPincode(type);
      if (chargeInput) chargeInput.value = String(amt);
      if (chargeCopy) chargeCopy.hidden = !chargeApplies() || studio; // Home/Office charge copy only
      // The payment panel shows the selected type's fee and wording.
      if (payAmount) payAmount.textContent = inr(amt);
      if (payLabel) payLabel.textContent = studio ? 'Appointment fee' : 'Visiting charge';
      if (payBtnLabel) payBtnLabel.textContent = 'Pay ' + inr(amt) + ' to confirm ';
      if (payVisitCopy) payVisitCopy.hidden = studio;
      if (payStudioCopy) payStudioCopy.hidden = !studio;
    }

    function applyType() {
      var type = selectedType();
      verified = false;
      if (pinInput) pinInput.value = '';
      if (pinField) pinField.removeAttribute('data-valid');
      setMessage('', '');
      clearInvalid(form.querySelector('[name="address"]'));

      if (requiresPincode(type)) {
        if (locationFields) locationFields.hidden = false;
        showGated(false); // gate details until a serviceable pincode is verified
        if (typeNote) typeNote.textContent = postPurchase
          ? 'As an existing purchaser, this visit is complimentary, with no visiting charge.'
          : 'Home / Office visits carry a ₹5,000 visiting charge (see below).';
      } else {
        if (locationFields) locationFields.hidden = true;
        showGated(true); // Ardas Studio needs no pincode
        if (typeNote) typeNote.textContent = postPurchase
          ? 'As an existing purchaser, your Ardas Studio visit is complimentary.'
          : 'Ardas Studio appointments carry a ₹1,500 fee, adjusted against your next ARDAS purchase.';
      }
      syncCharge();
    }

    function validatePincode() {
      var type = selectedType();
      if (!requiresPincode(type)) return;
      var value = (pinInput.value || '').replace(/\D/g, '').slice(0, 6);
      pinInput.value = value;
      if (value.length < 6) { verified = false; pinField.removeAttribute('data-valid'); showGated(false); setMessage('', ''); return; }
      if (approvedList(type).indexOf(value) !== -1) {
        verified = true; pinField.setAttribute('data-valid', 'true');
        setMessage('Pincode confirmed - we serve your area. Please continue below.', 'ok'); showGated(true);
      } else {
        verified = false; pinField.removeAttribute('data-valid');
        setMessage('We do not currently offer ' + type.toLowerCase() + ' visits at this pincode. Please try another, or book an Ardas Studio visit.', 'error');
        showGated(false);
      }
    }

    /* --- Chip groups: single-select (type / mezure) or multi-select --------
     * A group marked [data-multi] (e.g. "Interested In") toggles each chip
     * independently and stores all active values comma-separated. All other
     * groups keep the original single-select behaviour. */
    form.querySelectorAll('[data-chips]').forEach(function (group) {
      var hidden = group.querySelector('input[type="hidden"]');
      var multi = group.hasAttribute('data-multi');
      group.querySelectorAll('.chip').forEach(function (chip) {
        chip.addEventListener('click', function () {
          if (multi) {
            chip.classList.toggle('active'); // independent select / deselect
            if (hidden) {
              var values = [];
              group.querySelectorAll('.chip.active').forEach(function (c) { values.push(c.textContent.trim()); });
              hidden.value = values.join(', ');
            }
            return;
          }
          group.querySelectorAll('.chip').forEach(function (c) { c.classList.remove('active'); });
          chip.classList.add('active');
          if (hidden) {
            if (chip.hasAttribute('data-type')) hidden.value = chip.getAttribute('data-type');
            else if (chip.hasAttribute('data-mezure')) hidden.value = chip.getAttribute('data-mezure');
            else hidden.value = chip.textContent.trim();
          }
          if (group === typeGroup) applyType();
          if (group.hasAttribute('data-mezure-status') && mezureNote) {
            mezureNote.hidden = chip.getAttribute('data-mezure') !== 'existing';
          }
        });
      });
    });

    if (pinInput) { pinInput.addEventListener('input', validatePincode); pinInput.addEventListener('blur', validatePincode); }

    var done = form.parentNode.querySelector('[data-done]');
    function showDone() {
      // Explicit confirmation for FREE flows (Studio / any post-purchase visit).
      // Paid Flow B (Home/Office) never reaches here - it stops at the payment
      // boundary - so no confirmation is faked for an unpaid Razorpay flow.
      form.style.transition = 'opacity .5s'; form.style.opacity = '0';
      setTimeout(function () {
        form.style.display = 'none';
        if (done) { done.hidden = false; requestAnimationFrame(function () { done.classList.add('in'); }); }
      }, 500);
    }
    function fieldError(el, msg) {
      // Surface the message on the pincode status line if present, else focus only.
      if (msg) setMessage(msg, 'error');
      focusFirst(el);
    }

    /* --- Submit: validation, pincode gate, payment boundary, confirmation ---
     * Capture phase so this runs before main.js's data-capture handler; every
     * path here calls preventDefault + stopImmediatePropagation so main.js never
     * double-handles or shows a confirmation for a paid/invalid submission. */
    form.addEventListener('submit', function (e) {
      e.preventDefault(); e.stopImmediatePropagation();
      var type = selectedType();

      // Home/Office need a verified serviceable pincode before anything else.
      if (requiresPincode(type) && !verified) {
        if (locationFields) locationFields.hidden = false;
        fieldError(pinInput, pinMsg && pinMsg.textContent ? null : 'Please enter a serviceable pincode to continue.');
        return;
      }

      // Validate every field in page order (form is novalidate), show each error
      // under its field, then move to the first one.
      var names = (requiresPincode(type) ? ['address'] : []).concat(['name', 'phone', 'email', 'preferred_date', 'preferred_time']);
      var firstInvalid = null;
      names.forEach(function (n) {
        var el = form.querySelector('[name="' + n + '"]');
        if (el && checkField(el) && !firstInvalid) firstInvalid = el;
      });

      // Consent (required): must be checked before ANY booking/payment path runs.
      var consentEl = form.querySelector('[data-consent]');
      if (consentEl && !consentEl.checked) {
        markInvalid(consentEl, 'Please agree to the processing of your personal information to continue.');
        if (!firstInvalid) firstInvalid = consentEl;
      } else clearInvalid(consentEl);

      if (firstInvalid) { focusFirst(firstInvalid); return; }

      // Flow B (Studio, Home, Office) => fee => stop at the payment boundary
      // (no confirmation until real payment). Any post-purchase visit is free and
      // gets the confirmation immediately.
      if (chargeApplies()) {
        if (paymentStep) {
          form.style.transition = 'opacity .4s'; form.style.opacity = '0';
          setTimeout(function () {
            form.style.display = 'none';
            paymentStep.hidden = false;
            requestAnimationFrame(function () {
              paymentStep.classList.add('in');
              // Hiding the tall form shrinks the page and can leave the viewport
              // parked near the footer; recentre the payment panel so it stays in
              // view. Instant reposition (no added scroll animation).
              paymentStep.scrollIntoView({ block: 'center' });
            });
          }, 400);
        }
        return;
      }
      submitFreeBooking();
    }, true);

    /* Free bookings (Ardas Studio / any post-purchase visit): persist to the
     * backend so they appear in admin + trigger emails, then confirm. If the
     * backend is not configured, fall back to the original client-only confirm
     * so the free flow never breaks. */
    /* The backend confirms a complimentary visit only for a verified paid order
     * (one visit per order), so success is shown only when it accepts; otherwise
     * its reason is shown with a link to book with the standard fee. */
    var freeErr = null;
    function showFreeError(msg) {
      var submitBtn = form.querySelector('[data-booking-submit]');
      if (!freeErr) {
        freeErr = document.createElement('p');
        freeErr.className = 'booking__err'; freeErr.setAttribute('role', 'alert');
        if (submitBtn) submitBtn.parentNode.insertBefore(freeErr, submitBtn.nextSibling);
      }
      var paid = new URLSearchParams();
      if (params.get('type')) paid.set('type', selectedType());
      freeErr.innerHTML = '';
      freeErr.appendChild(document.createTextNode(msg + ' '));
      var link = document.createElement('a');
      link.href = window.location.pathname + (paid.toString() ? '?' + paid.toString() : '');
      link.textContent = 'Book with the standard appointment fee';
      link.style.textDecoration = 'underline';
      freeErr.appendChild(link);
      freeErr.hidden = false;
      if (submitBtn) submitBtn.disabled = false;
    }
    function submitFreeBooking() {
      if (!PAY_CFG.apiBase) { showDone(); return; }
      var submitBtn = form.querySelector('[data-booking-submit]');
      if (submitBtn) submitBtn.disabled = true;
      if (freeErr) freeErr.hidden = true;
      fetch(apiUrl('/create-booking'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appointment: gatherAppointment() })
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (out) {
          if (r.ok && out && out.ok) { showDone(); return; }
          showFreeError((out && out.message) || 'We could not confirm a complimentary appointment.');
        });
      }, function () {
        showFreeError('We could not reach the booking service. Please check your connection and try again.');
      });
    }

    if (payBack && paymentStep) {
      payBack.addEventListener('click', function () {
        paymentStep.hidden = true; paymentStep.classList.remove('in');
        form.style.display = ''; form.style.opacity = '';
      });
    }

    /* --- Payment button (Flow B, Home/Office) ------------------------------
     * Enabled only when a secure backend is configured. Gathers the appointment
     * data and hands off to ARDAS_PAYMENT.pay(); the confirmation is shown only
     * after server-verified success. No success is ever faked. */
    var payBtn = paymentStep && paymentStep.querySelector('[data-razorpay-pay]');
    var payNote = paymentStep && paymentStep.querySelector('[data-pay-note]');
    function setPayNote(text, state) {
      if (!payNote) return;
      payNote.textContent = text;
      if (state) payNote.setAttribute('data-state', state); else payNote.removeAttribute('data-state');
    }
    function gatherAppointment() {
      var data = {};
      ['appointment_type', 'pincode', 'address', 'mezure_status', 'interest',
       'name', 'phone', 'email', 'preferred_date', 'preferred_time', 'notes',
       'flow', 'visiting_charge'].forEach(function (n) {
        var el = form.querySelector('[name="' + n + '"]');
        if (el) data[n] = el.value;
      });
      // Explicit consent captured with the booking (validated before submit).
      var consentEl = form.querySelector('[data-consent]');
      data.consent = !!(consentEl && consentEl.checked);
      data.consent_timestamp = data.consent ? new Date().toISOString() : '';
      data.privacy_policy_version = 'current';
      // Tells the backend this page shows its refusal reasons (older tabs don't).
      data.client = 'booking-v2';
      return data;
    }
    if (payBtn) {
      if (window.ARDAS_PAYMENT && window.ARDAS_PAYMENT.configured) {
        payBtn.disabled = false;
        payBtn.removeAttribute('aria-disabled');
        setPayNote('You will be redirected to a secure Razorpay payment. Your appointment is held pending payment confirmation.');
        payBtn.addEventListener('click', function () {
          payBtn.disabled = true; payBtn.setAttribute('aria-disabled', 'true'); // prevent duplicate orders
          setPayNote('Opening secure payment…');
          var reEnable = function () {
            payBtn.disabled = false; payBtn.removeAttribute('aria-disabled');
          };
          window.ARDAS_PAYMENT.pay({
            appointment: gatherAppointment(),
            description: requiresPincode(selectedType()) ? 'Mezure visiting charge' : 'Ardas Studio appointment fee',
            onSuccess: function () {
              paymentStep.hidden = true; paymentStep.classList.remove('in');
              showDone();
            },
            onFailure: function (err) {
              reEnable();
              setPayNote((err && err.message ? err.message + ' ' : '') + 'Please try again.', 'error');
            }
          });
        });
      } else {
        // Backend not live yet: keep disabled, no payment path exposed.
        payBtn.disabled = true; payBtn.setAttribute('aria-disabled', 'true');
      }
    }

    var dateEl = form.querySelector('[name="preferred_date"]');
    if (dateEl) dateEl.min = todayISO(); // date picker starts at today

    applyType(); // initialise

    /* --- URL prefill (from the landing-page booking links) ---------------------
     * ?type=Home|Office|Ardas Studio  ?pincode=6 digits  ?garment=<Interested In chip>
     * Only selects an existing appointment-type chip, fills the pincode (Home/Office)
     * through the normal pincode check, and ticks a matching Interested In chip.
     * Unknown values are ignored. Never reads an amount or the free (post-purchase)
     * flow: prices stay fixed on the payment backend. */
    (function applyPrefill() {
      var type = params.get('type');
      var typeChip = type && typeGroup && Array.prototype.find.call(typeGroup.querySelectorAll('.chip[data-type]'), function (c) { return c.getAttribute('data-type') === type; });
      if (typeChip && !typeChip.classList.contains('active')) typeChip.click();
      var pin = (params.get('pincode') || '').replace(/\D/g, '');
      if (pin.length === 6 && pinInput && requiresPincode(selectedType())) { pinInput.value = pin; validatePincode(); }
      var garment = params.get('garment');
      var interest = form.querySelector('[data-chips][data-multi]');
      if (garment && interest) {
        interest.querySelectorAll('.chip').forEach(function (c) {
          if (c.textContent.trim() === garment && !c.classList.contains('active')) c.click();
        });
      }
    })();
  });
})();
